import { forwardRef } from 'react';
import { Tag as AntdTag } from 'antd';
import styles from './Tag.module.css';

/**
 * @typedef {'pink' | 'magenta' | 'red' | 'volcano' | 'orange' | 'gold' | 'yellow' | 'lime' |
 *   'green' | 'cyan' | 'blue' | 'geekblue' | 'purple' | 'success' | 'processing' | 'error' |
 *   'warning' | 'default'} TagColor
 */

/**
 * Accessible colors for the `filled`/`outlined` variants — antd's raw preset colors don't meet
 * WCAG AA contrast.
 */
const ACCESSIBLE_COLORS = {
    pink: { background: '#ffe4f0', borderColor: '#ffadd2', color: '#780650' },
    magenta: { background: '#ffe4f0', borderColor: '#ffadd2', color: '#780650' },
    red: { background: '#ffe4e4', borderColor: '#ffa39e', color: '#820014' },
    volcano: { background: '#ffe7e0', borderColor: '#ffad9f', color: '#7c2206' },
    orange: { background: '#fff3e0', borderColor: '#ffd591', color: '#7c4100' },
    gold: { background: '#fff8e0', borderColor: '#ffe58f', color: '#614700' },
    yellow: { background: '#fff8e0', borderColor: '#ffe58f', color: '#614700' },
    lime: { background: '#f0ffe0', borderColor: '#d3f261', color: '#3a5c00' },
    green: { background: '#e0fce0', borderColor: '#95de64', color: '#135200' },
    cyan: { background: '#e0f8f8', borderColor: '#87e8de', color: '#00474f' },
    blue: { background: '#e0eeff', borderColor: '#91caff', color: '#003998' },
    geekblue: { background: '#e8eeff', borderColor: '#adb8ff', color: '#061178' },
    purple: { background: '#f2e8ff', borderColor: '#d3adf7', color: '#391085' },
    success: {
        background: 'var(--color-feedback-success-bg)',
        borderColor: 'var(--color-feedback-success-text)',
        color: 'var(--color-feedback-success-text)',
    },
    processing: {
        background: 'var(--color-primary-100)',
        borderColor: 'var(--color-primary-700)',
        color: 'var(--color-primary-700)',
    },
    error: {
        background: 'var(--color-feedback-error-bg)',
        borderColor: 'var(--color-feedback-error-text)',
        color: 'var(--color-feedback-error-text)',
    },
    warning: {
        background: 'var(--color-feedback-warning-bg)',
        borderColor: 'var(--color-feedback-warning-text)',
        color: 'var(--color-feedback-warning-text)',
    },
    default: {
        background: 'var(--color-surface-raised)',
        borderColor: 'var(--color-border-strong)',
        color: 'var(--color-text-primary)',
    },
};

/**
 * Accessible colors for the `solid` variant.
 */
const SOLID_COLORS = {
    pink: { background: '#c41d7f', color: '#ffffff' },
    magenta: { background: '#c41d7f', color: '#ffffff' },
    red: { background: '#cf1322', color: '#ffffff' },
    volcano: { background: '#d4380d', color: '#ffffff' },
    orange: { background: '#ad4e00', color: '#ffffff' },
    gold: { background: '#874d00', color: '#ffffff' },
    yellow: { background: '#876800', color: '#ffffff' },
    lime: { background: '#3f6600', color: '#ffffff' },
    green: { background: '#237804', color: '#ffffff' },
    cyan: { background: '#006d75', color: '#ffffff' },
    blue: { background: '#0958d9', color: '#ffffff' },
    geekblue: { background: '#1d39c4', color: '#ffffff' },
    purple: { background: '#531dab', color: '#ffffff' },
    success: { background: 'var(--color-feedback-success-text)', color: 'var(--color-text-on-brand)' },
    processing: { background: 'var(--color-primary-700)', color: 'var(--color-text-on-brand)' },
    error: { background: 'var(--color-feedback-error-text)', color: 'var(--color-text-on-brand)' },
    warning: { background: 'var(--color-feedback-warning-text)', color: 'var(--color-text-on-brand)' },
    default: { background: 'var(--color-neutral-700)', color: 'var(--color-text-on-brand)' },
};

/**
 * @typedef {Object} TagOwnProps
 * @property {TagColor} [color] - A key into `ACCESSIBLE_COLORS`/`SOLID_COLORS`. Any other string
 *   (a custom hex/rgb) passes straight through to antd's own `color` prop unchanged.
 * @property {'filled' | 'outlined' | 'solid'} [variant='filled']
 * @property {(event: import('react').MouseEvent) => void} [onClick] - When present, the tag is
 *   wrapped in a real `<button>` — antd's `Tag` renders a `<span>`, so a bare `onClick` there
 *   isn't keyboard-operable or announced as a button.
 */

/** @typedef {TagOwnProps & Omit<import('antd').TagProps, 'color' | 'variant'>} TagProps */

/**
 * @param {TagProps} props
 */
const Tag = forwardRef(({ color, variant = 'filled', style, onClick, ...props }, ref) => {
    const preset = color && ACCESSIBLE_COLORS[color];
    const tagRef = onClick ? undefined : ref;

    let tag;
    if (!preset) {
        tag = <AntdTag ref={tagRef} color={color} variant={variant} style={style} {...props} />;
    } else if (variant === 'solid') {
        tag = <AntdTag ref={tagRef} variant="solid" style={{ ...SOLID_COLORS[color], ...style }} {...props} />;
    } else {
        const { borderColor, ...rest } = preset;
        const colorStyle = variant === 'outlined' ? { ...rest, borderColor } : rest;
        tag = <AntdTag ref={tagRef} variant={variant} style={{ ...colorStyle, ...style }} {...props} />;
    }

    if (!onClick) {
        return tag;
    }

    return (
        <button ref={ref} type="button" onClick={onClick} className={styles.vecTagButton}>
            {tag}
        </button>
    );
});
Tag.displayName = 'Tag';

export default Tag;
